/* Claro CMS — "Add a section" picker.
   Replaces the flat Add dropdown with a searchable sheet of section cards,
   grouped by user intent, each with a mini wireframe preview, a "Best for…"
   line, and Required / Repeatable / Already-used badges. Driven entirely by the
   real module catalog so advisors can recognise a section by its shape. */
import { useEffect, useMemo, useState } from 'react';
import { LuLayers, LuPlus, LuSearch, LuX } from 'react-icons/lu';

import { CmsPageModuleContent, CmsPageModuleType } from '@ndvr/cms-api';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/utils/cn';

import { getAddableCmsModuleTypes, isRepeatableCmsModuleType, isRequiredCmsModuleType } from './cms-editor-state';
import {
    CMS_SECTION_CATALOG,
    CMS_SECTION_GROUP_OF,
    CMS_SECTION_GROUPS,
    CmsSectionCatalogEntry,
    CmsSectionGroupKey,
    CmsSectionWireframe,
} from './cms-section-catalog';

const GROUP_DOT_TONE: Record<string, string> = {
    aqua: 'bg-seafoam-400',
    gold: 'bg-gold-400',
    cocoa: 'bg-cocoa-900',
};

interface SectionBadgesProps {
    type: CmsPageModuleType;
    used: number;
}

const SectionBadges = ({ type, used }: SectionBadgesProps) => (
    <span className="flex flex-wrap items-center gap-1.5">
        {isRequiredCmsModuleType(type) && (
            <span className="bg-gold-100 text-gold-600 rounded-full px-1.5 py-0.5 text-[9.5px] font-bold tracking-wide uppercase">
                Required
            </span>
        )}
        {isRepeatableCmsModuleType(type) && (
            <span className="text-muted-foreground rounded-full px-1.5 py-0.5 text-[9.5px] font-bold tracking-wide uppercase ring-1 ring-stone-300 ring-inset">
                Repeatable
            </span>
        )}
        {used > 0 && (
            <span className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[9.5px] font-bold tracking-wide text-stone-500 uppercase">
                {used > 1 ? `${used}× on page` : 'Already used'}
            </span>
        )}
    </span>
);

interface SectionCardProps {
    entry: CmsSectionCatalogEntry;
    used: number;
    disabled: boolean;
    showGroup: boolean;
    onAdd: (type: CmsPageModuleType) => void;
}

const SectionCard = ({ entry, used, disabled, showGroup, onAdd }: SectionCardProps) => {
    const Icon = entry.icon;
    const group = CMS_SECTION_GROUP_OF[entry.group];
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={() => onAdd(entry.type)}
            className={cn(
                'group flex flex-col overflow-hidden rounded-lg border bg-white text-left transition-colors',
                disabled
                    ? 'cursor-not-allowed opacity-55'
                    : 'hover:border-cocoa-900 cursor-pointer border-stone-200 hover:shadow-md active:translate-y-px',
            )}
        >
            <CmsSectionWireframe kind={entry.wire} />
            <div className="flex flex-col gap-2 px-3.5 pt-3 pb-3.5">
                <div className="flex items-center gap-2.5">
                    <span className="bg-seafoam-100 text-cocoa-900 flex h-7 w-7 shrink-0 items-center justify-center rounded">
                        <Icon className="h-4 w-4" />
                    </span>
                    <span className="flex-1 text-sm font-semibold">{entry.name}</span>
                    {!disabled && <LuPlus className="text-cocoa-300 group-hover:text-seafoam-400 h-4 w-4 shrink-0" />}
                </div>
                <p className="text-muted-foreground m-0 text-xs leading-relaxed text-pretty">{entry.bestFor}</p>
                <div className="flex flex-wrap items-center gap-1.5">
                    <SectionBadges type={entry.type} used={used} />
                    {showGroup && (
                        <span className="text-muted-foreground ml-auto text-[10px] font-semibold tracking-wide uppercase">
                            {group.label}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
};

interface CmsSectionPickerProps {
    open: boolean;
    modules: CmsPageModuleContent[];
    onAdd: (type: CmsPageModuleType) => void;
    onOpenChange: (open: boolean) => void;
}

export const CmsSectionPicker = ({ open, modules, onAdd, onOpenChange }: CmsSectionPickerProps) => {
    const [query, setQuery] = useState('');
    const [groupKey, setGroupKey] = useState<CmsSectionGroupKey | 'all'>('all');

    useEffect(() => {
        if (open) {
            setQuery('');
            setGroupKey('all');
        }
    }, [open]);

    const addableTypes = useMemo(() => new Set(getAddableCmsModuleTypes(modules)), [modules]);
    const usedCountOf = (type: CmsPageModuleType) => modules.filter((module) => module.moduleType === type).length;

    const add = (type: CmsPageModuleType) => {
        onAdd(type);
        onOpenChange(false);
    };

    const q = query.trim().toLowerCase();
    const matches = (entry: CmsSectionCatalogEntry) =>
        !q || `${entry.name} ${entry.bestFor} ${CMS_SECTION_GROUP_OF[entry.group].label}`.toLowerCase().includes(q);

    const renderCard = (entry: CmsSectionCatalogEntry, showGroup: boolean) => (
        <SectionCard
            key={entry.type}
            entry={entry}
            used={usedCountOf(entry.type)}
            disabled={!addableTypes.has(entry.type)}
            showGroup={showGroup}
            onAdd={add}
        />
    );

    let content;
    if (q) {
        const found = CMS_SECTION_CATALOG.filter(matches);
        content = (
            <div>
                <h4 className="mb-4 font-serif text-lg font-normal">
                    {found.length} {found.length === 1 ? 'section' : 'sections'} matching “{query}”
                </h4>
                {found.length ? (
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3.5">
                        {found.map((entry) => renderCard(entry, true))}
                    </div>
                ) : (
                    <div className="text-muted-foreground flex flex-col items-center gap-2 py-14 text-center">
                        <LuSearch className="h-6 w-6" />
                        <p className="m-0">No sections match “{query}”.</p>
                    </div>
                )}
            </div>
        );
    } else {
        const groups = groupKey === 'all' ? CMS_SECTION_GROUPS : CMS_SECTION_GROUPS.filter((g) => g.key === groupKey);
        content = (
            <div className="space-y-9">
                {groups.map((group) => {
                    const entries = CMS_SECTION_CATALOG.filter((entry) => entry.group === group.key);
                    if (!entries.length) {
                        return null;
                    }
                    return (
                        <div key={group.key}>
                            <div className="mb-4">
                                <h4 className="m-0 mb-0.5 flex items-center gap-2.5 font-serif text-lg font-normal">
                                    <span className={cn('h-2 w-2 shrink-0 rounded-full', GROUP_DOT_TONE[group.tone])} />
                                    {group.label}
                                </h4>
                                <span className="text-muted-foreground text-xs">{group.sub}</span>
                            </div>
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3.5">
                                {entries.map((entry) => renderCard(entry, false))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex h-[min(760px,calc(100vh-64px))] w-full max-w-[1000px] flex-col gap-0 overflow-hidden bg-stone-50 p-0">
                <div className="flex items-center gap-5 border-b bg-white px-6 py-4">
                    <div className="flex shrink-0 flex-col gap-0.5">
                        <DialogTitle className="font-serif text-2xl font-normal tracking-normal">
                            Add a section
                        </DialogTitle>
                        <DialogDescription className="text-xs">Choose a building block for your page</DialogDescription>
                    </div>
                    <div className="focus-within:border-cocoa-900 mr-8 ml-auto flex max-w-[420px] flex-1 items-center gap-2.5 rounded-full border border-stone-300 bg-stone-50 px-4 py-2">
                        <LuSearch className="text-muted-foreground h-4 w-4 shrink-0" />
                        <Input
                            autoFocus
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Search sections…"
                            className="h-auto border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
                        />
                        {query && (
                            <button
                                type="button"
                                aria-label="Clear search"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => setQuery('')}
                            >
                                <LuX className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex min-h-0 flex-1">
                    {!q && (
                        <nav className="w-[200px] shrink-0 space-y-0.5 overflow-y-auto border-r p-3.5">
                            <button
                                type="button"
                                onClick={() => setGroupKey('all')}
                                className={cn(
                                    'flex w-full items-center gap-2.5 rounded px-3 py-2 text-left text-sm font-medium transition-colors',
                                    groupKey === 'all'
                                        ? 'bg-cocoa-900 text-white'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-stone-100',
                                )}
                            >
                                <LuLayers className="h-4 w-4 shrink-0" />
                                <span>All sections</span>
                            </button>
                            <div className="my-3 h-px bg-stone-200" />
                            <div className="text-muted-foreground px-3 pb-2 text-[10.5px] font-semibold tracking-wider uppercase">
                                By intent
                            </div>
                            {CMS_SECTION_GROUPS.map((group) => (
                                <button
                                    key={group.key}
                                    type="button"
                                    onClick={() => setGroupKey(group.key)}
                                    className={cn(
                                        'flex w-full items-center gap-2.5 rounded px-3 py-2 text-left text-sm font-medium transition-colors',
                                        groupKey === group.key
                                            ? 'bg-cocoa-900 text-white'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-stone-100',
                                    )}
                                >
                                    <span className={cn('h-2 w-2 shrink-0 rounded-full', GROUP_DOT_TONE[group.tone])} />
                                    <span>{group.label}</span>
                                </button>
                            ))}
                        </nav>
                    )}
                    <div className="flex-1 overflow-y-auto px-7 py-6">{content}</div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
